import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

export interface WeightRegistrationData {
  outgoingPackaging: string;
  existingWeightRegistrations: Array<{
    [key: string]: any;
    lotNumber?: string;
  }>;
  palletsAmount: number;
  packagingAmount: number;
  palletCapacity: number;
  packingMaterialsLotNumber: string | null;
}

export interface WeightRegistrationResult {
  lotNumber: string;
  palletNumber: number;
  outgoingPackaging: string;
  outgoingPackagingAmount: number;
  weight: number;
  qis: boolean;
}

@Component({
  selector: 'app-weight-registration-dialog',
  templateUrl: './weight-registration-dialog.component.html',
  styleUrls: ['./weight-registration-dialog.component.scss'],
})
export class WeightRegistrationDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  outgoingPackaging = '';
  isLotNumberLocked = false;
  weightValue: number | string = 0;
  private destroyed$ = new Subject<void>();

  private palletsAmount = 0;
  private packagingAmount = 0;
  private palletCapacity = 0;
  private readonly weightStep = 0.1;

  constructor(
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public readonly data: WeightRegistrationData,
    private readonly dialogRef: MatDialogRef<
      WeightRegistrationDialogComponent,
      WeightRegistrationResult
    >
  ) {
    this.outgoingPackaging = data?.outgoingPackaging || '';
    this.palletsAmount = data?.palletsAmount || 0;
    this.packagingAmount = data?.packagingAmount || 0;
    this.palletCapacity = data?.palletCapacity || 0;

    const existingRegistrations = data?.existingWeightRegistrations || [];
    const hasExistingRegistrations = existingRegistrations.length > 0;
    const packingMaterialsLotNumber = data?.packingMaterialsLotNumber;

    this.isLotNumberLocked =
      !!packingMaterialsLotNumber || hasExistingRegistrations;

    const initialLotNumber = packingMaterialsLotNumber
      ? packingMaterialsLotNumber
      : hasExistingRegistrations
      ? existingRegistrations[0]?.lotNumber || ''
      : '';

    const initialPalletNumber = this.calculatePalletNumber(0);

    this.form = fb.group({
      lotNumber: [
        { value: initialLotNumber, disabled: this.isLotNumberLocked },
        this.isLotNumberLocked ? [] : [Validators.required],
      ],
      palletNumber: [initialPalletNumber, [Validators.min(0)]],
      outgoingPackagingAmount: [0, [Validators.min(0)]],
      weight: [0, [Validators.min(0)]],
      qisYes: [true],
      qisNo: [false],
    });
  }

  public get canSubmit(): boolean {
    return this.form.valid;
  }

  public get isWeightAtMin(): boolean {
    const numValue = parseFloat(String(this.weightValue).replace(',', '.'));
    return isNaN(numValue) || numValue <= 0;
  }

  ngOnInit(): void {
    this.form
      .get('outgoingPackagingAmount')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        const outgoingPackagingAmount = value || 0;
        const calculatedPalletNumber = this.calculatePalletNumber(
          outgoingPackagingAmount
        );
        this.form
          .get('palletNumber')
          ?.setValue(calculatedPalletNumber, { emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onQisChange(option: 'yes' | 'no', checked: boolean): void {
    if (checked) {
      if (option === 'yes') {
        this.form.get('qisNo')?.setValue(false);
      } else {
        this.form.get('qisYes')?.setValue(false);
      }
    }
  }

  public onWeightInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.weightValue = input.value;
  }

  public onWeightBlur(): void {
    const numValue = parseFloat(String(this.weightValue).replace(',', '.'));
    if (!isNaN(numValue) && numValue >= 0) {
      this.weightValue = Math.round(numValue * 10) / 10;
    } else {
      this.weightValue = 0;
    }
    this.form.get('weight')?.setValue(this.weightValue, { emitEvent: false });
  }

  public incrementWeight(): void {
    const currentValue =
      parseFloat(String(this.weightValue).replace(',', '.')) || 0;
    this.weightValue = Math.round((currentValue + this.weightStep) * 10) / 10;
    this.form.get('weight')?.setValue(this.weightValue, { emitEvent: false });
  }

  public decrementWeight(): void {
    const currentValue =
      parseFloat(String(this.weightValue).replace(',', '.')) || 0;
    const newValue = currentValue - this.weightStep;
    this.weightValue = newValue >= 0 ? Math.round(newValue * 10) / 10 : 0;
    this.form.get('weight')?.setValue(this.weightValue, { emitEvent: false });
  }

  public submit(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      const weightValue =
        parseFloat(String(formValue.weight).replace(',', '.')) || 0;

      this.dialogRef.close({
        lotNumber: formValue.lotNumber,
        palletNumber: formValue.palletNumber,
        outgoingPackaging: this.outgoingPackaging,
        outgoingPackagingAmount: formValue.outgoingPackagingAmount,
        weight: Math.round(weightValue * 10) / 10,
        qis: formValue.qisYes === true,
      });
    }
  }

  private calculatePalletNumber(outgoingPackagingAmount: number): number {
    if (this.palletCapacity <= 0) {
      return this.palletsAmount;
    }
    const totalPackaging = outgoingPackagingAmount + this.packagingAmount;
    return Math.ceil(totalPackaging / this.palletCapacity);
  }
}
